import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import Editor from "../editor/Editor";

function getInitialState(initialValues) {
  return {
    to_email: initialValues?.to_email || "",
    subject: initialValues?.subject || "",
    body: initialValues?.body || "",
  };
}

function stripHtml(html) {
  return (html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function MailComposeDialog({
  open,
  title,
  confirmLabel,
  onClose,
  onSubmit,
  loading = false,
  initialValues,
  helperText,
  lockToEmail = false,
  hideToEmailField = false,
  recipientPreviewList = [],
  quotedHistoryHtml = "",
}) {
  const [form, setForm] = useState(getInitialState(initialValues));

  useEffect(() => {
    if (open) {
      setForm(getInitialState(initialValues));
    }
  }, [initialValues, open]);

  const isValid = useMemo(() => {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      form.to_email?.trim() || ""
    );

    return isValidEmail && !!form.subject?.trim() && !!stripHtml(form.body);
  }, [form]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (!isValid || loading) {
      return;
    }

    onSubmit({
      to_email: form.to_email.trim(),
      subject: form.subject.trim(),
      body: form.body,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {!hideToEmailField && (
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            variant="outlined"
            label="Destinatario"
            type="email"
            value={form.to_email}
            onChange={handleChange("to_email")}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: lockToEmail }}
          />
        )}

        {!!helperText && (
          <div
            style={{
              marginTop: hideToEmailField ? "8px" : "16px",
              marginBottom: recipientPreviewList.length ? "12px" : "0",
              color: "#3F4254",
              fontSize: hideToEmailField ? "1rem" : "0.85rem",
              fontWeight: hideToEmailField ? 500 : 400,
            }}
          >
            {helperText}
          </div>
        )}

        {!!hideToEmailField && !!recipientPreviewList.length && (
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                marginBottom: "8px",
                fontSize: "0.9rem",
                color: "#7E8299",
                fontWeight: 500,
              }}
            >
              Destinatarios
            </div>
            <div
              style={{
                border: "1px solid #E4E6EF",
                borderRadius: "0.42rem",
                padding: "12px 16px",
                backgroundColor: "#F8F9FB",
              }}
            >
              {recipientPreviewList.map((recipient, index) => (
                <div
                  key={`${recipient}-${index}`}
                  style={{
                    color: "#3F4254",
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                  }}
                >
                  {recipient}
                </div>
              ))}
            </div>
          </div>
        )}

        <TextField
          fullWidth
          margin="normal"
          variant="outlined"
          label="Asunto"
          value={form.subject}
          onChange={handleChange("subject")}
          InputLabelProps={{ shrink: true }}
        />

        <div style={{ marginTop: "16px" }}>
          <div
            style={{
              marginBottom: "8px",
              fontSize: "0.9rem",
              color: "#7E8299",
              fontWeight: 500,
            }}
          >
            Cuerpo
          </div>
          <Editor
            body={form.body}
            setBody={(body) =>
              setForm((current) => ({
                ...current,
                body,
              }))
            }
            className="mail-compose-editor"
            lang="es"
            placeholder="Escribe aqui el contenido del mail..."
          />
        </div>

        {!!quotedHistoryHtml && (
          <div style={{ marginTop: "24px" }}>
            <div
              style={{
                marginBottom: "8px",
                fontSize: "0.9rem",
                color: "#7E8299",
                fontWeight: 500,
              }}
            >
              Historico del mail
            </div>
            <div
              style={{
                minHeight: "180px",
                maxHeight: "280px",
                overflowY: "auto",
                border: "1px solid #E4E6EF",
                borderRadius: "0.42rem",
                padding: "16px",
                backgroundColor: "#F8F9FB",
              }}
              dangerouslySetInnerHTML={{ __html: quotedHistoryHtml }}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="outlined"
          disabled={!isValid || loading}
        >
          {loading ? "Enviando..." : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
